import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Avatar,
  Tooltip,
  LinearProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  InputAdornment,
} from '@mui/material';
import {
  Hotel as ReservationIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckInIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  AttachMoney as PaymentIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  LocalParking as ParkingIcon,
  Pets as PetsIcon,
  Group as GroupIcon,
  Payment as PaymentMethodIcon,
  Receipt as InvoiceIcon,
  Message as NoteIcon,
  History as HistoryIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Warning as WarningIcon,
  EventAvailable as ConfirmIcon,
  EventBusy as BlockIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import api from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reservations-tabpanel-${index}`}
      aria-labelledby={`reservations-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const StatusChip = ({ status }: { status: string }) => {
  const getColor = () => {
    switch (status) {
      case 'Confirmed': return 'success';
      case 'Pending': return 'warning';
      case 'Checked-In': return 'info';
      case 'Checked-Out': return 'default';
      case 'Cancelled': return 'error';
      case 'No-Show': return 'error';
      default: return 'default';
    }
  };

  return <Chip label={status} color={getColor()} size="small" />;
};

const PaymentStatusChip = ({ status }: { status: string }) => {
  const getColor = () => {
    switch (status) {
      case 'Paid': return 'success';
      case 'Partial': return 'warning';
      case 'Pending': return 'default';
      case 'Refunded': return 'error';
      default: return 'default';
    }
  };

  return <Chip label={status} color={getColor()} size="small" variant="outlined" />;
};

export default function ReservationsPage() {
  const [currentTab, setCurrentTab] = useState(0);
  const [reservations, setReservations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<any>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    status: 'all',
    dateRange: 'today',
    startDate: dayjs(),
    endDate: dayjs(),
    search: '',
    siteType: 'all',
    paymentStatus: 'all'
  });

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalReservations, setTotalReservations] = useState(0);

  // Dashboard stats
  const [dashboardStats, setDashboardStats] = useState({
    todayArrivals: 0,
    todayDepartures: 0,
    currentOccupancy: 0,
    totalRevenue: 0,
    pendingPayments: 0,
    upcomingReservations: 0
  });

  useEffect(() => {
    fetchDashboardStats();
    fetchReservations();
  }, [currentTab, filters, page, rowsPerPage]);

  const fetchDashboardStats = async () => {
    try {
      const response = await api.get('/reports/dashboard');
      if (response.data.success) {
        const data = response.data.data;
        setDashboardStats({
          todayArrivals: data.todayArrivals || 0,
          todayDepartures: data.todayDepartures || 0,
          currentOccupancy: data.occupancyRate || 0,
          totalRevenue: data.monthlyRevenue || 0,
          pendingPayments: data.pendingPayments || 0,
          upcomingReservations: data.upcomingReservations || 0
        });
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  const fetchReservations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString()
      });
      
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      if (filters.siteType !== 'all') params.append('siteType', filters.siteType);
      if (filters.paymentStatus !== 'all') params.append('paymentStatus', filters.paymentStatus);
      
      // Date filtering based on tab
      if (currentTab === 0) { // Today's arrivals
        params.append('checkInDate', dayjs().format('YYYY-MM-DD'));
      } else if (currentTab === 1) { // Today's departures
        params.append('checkOutDate', dayjs().format('YYYY-MM-DD'));
      } else if (currentTab === 2) { // Current guests
        params.append('status', 'Checked-In');
      }

      const response = await api.get(`/reservations?${params}`);
      setReservations(response.data.reservations || []);
      setTotalReservations(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching reservations:', error);
      setReservations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setPage(0);
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleReservationClick = async (reservation: any) => {
    try {
      const response = await api.get(`/reservations/${reservation._id}`);
      setSelectedReservation(response.data);
      setDetailsOpen(true);
    } catch (error) {
      console.error('Error fetching reservation details:', error);
    }
  };

  const handleStatusUpdate = async (reservationId: string, newStatus: string) => {
    try {
      await api.put(`/reservations/${reservationId}/status`, { status: newStatus });
      fetchReservations();
      if (selectedReservation?._id === reservationId) {
        setSelectedReservation({ ...selectedReservation, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating reservation status:', error);
    }
  };

  const handleCheckIn = async (reservationId: string) => {
    if (window.confirm('Check in this guest?')) {
      await handleStatusUpdate(reservationId, 'Checked-In');
    }
  };

  const handleCheckOut = async (reservationId: string) => {
    if (window.confirm('Check out this guest?')) {
      await handleStatusUpdate(reservationId, 'Checked-Out');
    }
  };

  const handleCancel = async (reservationId: string) => {
    if (window.confirm('Cancel this reservation? This action cannot be undone.')) {
      await handleStatusUpdate(reservationId, 'Cancelled');
    }
  };

  const renderDashboard = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Today's Arrivals
            </Typography>
            <Typography variant="h4">
              {dashboardStats.todayArrivals}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Today's Departures
            </Typography>
            <Typography variant="h4">
              {dashboardStats.todayDepartures}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Occupancy Rate
            </Typography>
            <Typography variant="h4">
              {dashboardStats.currentOccupancy}%
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Monthly Revenue
            </Typography>
            <Typography variant="h4">
              ${dashboardStats.totalRevenue.toLocaleString()}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Pending Payments
            </Typography>
            <Typography variant="h4">
              {dashboardStats.pendingPayments}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Upcoming
            </Typography>
            <Typography variant="h4">
              {dashboardStats.upcomingReservations}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderReservationsTable = () => (
    <Paper>
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search by guest name, email, or confirmation #"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="Confirmed">Confirmed</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Checked-In">Checked In</MenuItem>
                <MenuItem value="Checked-Out">Checked Out</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
                <MenuItem value="No-Show">No Show</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Site Type</InputLabel>
              <Select
                value={filters.siteType}
                onChange={(e) => handleFilterChange('siteType', e.target.value)}
                label="Site Type"
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="RV">RV</MenuItem>
                <MenuItem value="Tent">Tent</MenuItem>
                <MenuItem value="Cabin">Cabin</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Payment</InputLabel>
              <Select
                value={filters.paymentStatus}
                onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
                label="Payment"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="Paid">Paid</MenuItem>
                <MenuItem value="Partial">Partial</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <Stack direction="row" spacing={1}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => alert('Add reservation feature coming soon')}
              >
                New Booking
              </Button>
              <IconButton onClick={fetchReservations}>
                <RefreshIcon />
              </IconButton>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Confirmation #</TableCell>
              <TableCell>Guest</TableCell>
              <TableCell>Site</TableCell>
              <TableCell>Check In</TableCell>
              <TableCell>Check Out</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Payment</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9}>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            ) : reservations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography color="textSecondary">No reservations found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              reservations.map((reservation) => (
                <TableRow 
                  key={reservation._id}
                  hover
                  onClick={() => handleReservationClick(reservation)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {reservation.confirmationNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {reservation.guest?.firstName} {reservation.guest?.lastName}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {reservation.guest?.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {reservation.site?.siteNumber}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {reservation.site?.type}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {dayjs(reservation.checkIn).format('MMM D, YYYY')}
                  </TableCell>
                  <TableCell>
                    {dayjs(reservation.checkOut).format('MMM D, YYYY')}
                  </TableCell>
                  <TableCell>
                    <StatusChip status={reservation.status} />
                  </TableCell>
                  <TableCell>
                    <PaymentStatusChip status={reservation.paymentStatus} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      ${reservation.totalAmount?.toFixed(2) || '0.00'}
                    </Typography>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" spacing={1}>
                      {reservation.status === 'Confirmed' && (
                        <Tooltip title="Check In">
                          <IconButton 
                            size="small"
                            onClick={() => handleCheckIn(reservation._id)}
                          >
                            <CheckInIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {reservation.status === 'Checked-In' && (
                        <Tooltip title="Check Out">
                          <IconButton 
                            size="small"
                            onClick={() => handleCheckOut(reservation._id)}
                          >
                            <CheckInIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="View Details">
                        <IconButton size="small">
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      {['Confirmed', 'Pending'].includes(reservation.status) && (
                        <Tooltip title="Cancel">
                          <IconButton 
                            size="small"
                            onClick={() => handleCancel(reservation._id)}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalReservations}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Paper>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Reservations Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage all reservations, check-ins, and check-outs
        </Typography>
      </Box>

      {renderDashboard()}

      <Paper sx={{ mt: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab label="Today's Arrivals" />
          <Tab label="Today's Departures" />
          <Tab label="Current Guests" />
          <Tab label="All Reservations" />
          <Tab label="Calendar View" />
        </Tabs>
      </Paper>

      <TabPanel value={currentTab} index={0}>
        {renderReservationsTable()}
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        {renderReservationsTable()}
      </TabPanel>

      <TabPanel value={currentTab} index={2}>
        {renderReservationsTable()}
      </TabPanel>

      <TabPanel value={currentTab} index={3}>
        {renderReservationsTable()}
      </TabPanel>

      <TabPanel value={currentTab} index={4}>
        <Alert severity="info">
          Calendar view coming soon! Will show reservations in a monthly calendar format.
        </Alert>
      </TabPanel>

      {/* Reservation Details Dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Reservation Details - {selectedReservation?.confirmationNumber}
        </DialogTitle>
        <DialogContent>
          {selectedReservation && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Guest Information</Typography>
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary="Name"
                      secondary={`${selectedReservation.guest?.firstName} ${selectedReservation.guest?.lastName}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Email"
                      secondary={selectedReservation.guest?.email}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Phone"
                      secondary={selectedReservation.guest?.phone}
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Booking Details</Typography>
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary="Site"
                      secondary={`${selectedReservation.site?.siteNumber} (${selectedReservation.site?.type})`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Check In"
                      secondary={dayjs(selectedReservation.checkIn).format('MMMM D, YYYY')}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Check Out"
                      secondary={dayjs(selectedReservation.checkOut).format('MMMM D, YYYY')}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Guests"
                      secondary={selectedReservation.numberOfGuests}
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>Payment Information</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Subtotal:</Typography>
                  <Typography>${selectedReservation.subtotal?.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Taxes:</Typography>
                  <Typography>${selectedReservation.taxes?.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">Total:</Typography>
                  <Typography variant="h6">${selectedReservation.totalAmount?.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Amount Paid:</Typography>
                  <Typography color={selectedReservation.amountPaid >= selectedReservation.totalAmount ? 'success.main' : 'warning.main'}>
                    ${selectedReservation.amountPaid?.toFixed(2)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          <Button variant="contained" startIcon={<EditIcon />}>
            Edit Reservation
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}